import { useEffect, useRef, useState } from "react";
import styles from "./ChipDropdown.module.css";

interface Option {
    key: string;
    text: string;
}

interface Props {
    label: string;
    options: Option[];
    selectedKeys: string[];
    placeholder?: string;
    onChange: (selectedKeys: string[]) => void;
    isOpen?: boolean;
    onOpenChange?: (open: boolean) => void;
}

export const ChipDropdown = ({ label, options, selectedKeys, placeholder, onChange, isOpen: controlledOpen, onOpenChange }: Props) => {
    const [internalOpen, setInternalOpen] = useState(false);
    const isOpen = controlledOpen !== undefined ? controlledOpen : internalOpen;
    const [search, setSearch] = useState("");
    const containerRef = useRef<HTMLDivElement>(null);
    const searchRef = useRef<HTMLInputElement>(null);

    const setOpen = (open: boolean) => {
        if (onOpenChange) {
            onOpenChange(open);
        } else {
            setInternalOpen(open);
        }
        if (!open) setSearch("");
    };

    useEffect(() => {
        const handler = (e: MouseEvent) => {
            if (isOpen && containerRef.current && !containerRef.current.contains(e.target as Node)) {
                setOpen(false);
            }
        };
        document.addEventListener("mousedown", handler);
        return () => document.removeEventListener("mousedown", handler);
    }, [isOpen]);

    useEffect(() => {
        if (isOpen && searchRef.current) {
            searchRef.current.focus();
        }
    }, [isOpen]);

    const selectedSet = new Set(selectedKeys);

    const toggleOption = (key: string) => {
        if (selectedSet.has(key)) {
            onChange(selectedKeys.filter(k => k !== key));
        } else {
            onChange([...selectedKeys, key]);
        }
    };

    const removeChip = (key: string, e: React.MouseEvent) => {
        e.stopPropagation();
        onChange(selectedKeys.filter(k => k !== key));
    };

    const filteredOptions = options.filter(o => o.text.toLowerCase().includes(search.toLowerCase()));
    const valueMap = new Map(options.map(o => [o.key, o.text]));

    return (
        <div className={styles.wrapper} ref={containerRef}>
            <label className={styles.label}>{label}</label>
            <div className={`${styles.inputBox} ${isOpen ? styles.inputBoxOpen : ""}`} onClick={() => setOpen(true)}>
                <div className={styles.inputContent}>
                    {selectedKeys.length === 0 && !isOpen && <span className={styles.placeholder}>{placeholder ?? "All selected"}</span>}
                    {selectedKeys.map(key => (
                        <span key={key} className={styles.chip}>
                            <span className={styles.chipText}>{valueMap.get(key) ?? key}</span>
                            <button className={styles.chipRemove} onClick={e => removeChip(key, e)} aria-label={`Remove ${valueMap.get(key) ?? key}`}>
                                &#x2715;
                            </button>
                        </span>
                    ))}
                    {isOpen && (
                        <input
                            ref={searchRef}
                            className={styles.searchInput}
                            value={search}
                            onChange={e => setSearch(e.target.value)}
                            placeholder={selectedKeys.length === 0 ? "Search..." : ""}
                        />
                    )}
                </div>
                <button
                    className={`${styles.chevron} ${isOpen ? styles.chevronOpen : ""}`}
                    onClick={e => {
                        e.stopPropagation();
                        setOpen(!isOpen);
                    }}
                    aria-label="Toggle dropdown"
                    tabIndex={-1}
                >
                    <svg width="12" height="7" viewBox="0 0 12 7" fill="none" xmlns="http://www.w3.org/2000/svg">
                        <path d="M1 1L6 6L11 1" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
                    </svg>
                </button>
            </div>
            {isOpen && (
                <div className={styles.dropdown}>
                    {filteredOptions.length === 0 && <span className={styles.noResults}>No results</span>}
                    <div className={styles.optionsContainer}>
                        {filteredOptions.map(option => {
                            const isSelected = selectedSet.has(option.key);
                            return (
                                <button
                                    key={option.key}
                                    className={`${styles.optionChip} ${isSelected ? styles.optionChipSelected : ""}`}
                                    onClick={() => toggleOption(option.key)}
                                >
                                    {option.text}
                                    {isSelected && <span className={styles.optionCheck}>&#x2715;</span>}
                                </button>
                            );
                        })}
                    </div>
                </div>
            )}
        </div>
    );
};
